import { useTranslation } from 'react-i18next'
import { SwitchRow } from './SwitchRow'
import type { DetectorConfigOption, DetectorConfigPatch } from '@/types'

// person/vehicle/animal are structural — they always report, not per-species toggleable.
const STRUCTURAL = new Set(['coarse', 'fallback'])

/**
 * Owner-facing per-camera species enable/disable for the open-vocabulary
 * (speciesnet-bioclip) detector (#108). A disabled species is reported as the
 * generic "animal" rather than dropped, so the camera never goes blind.
 */
export function SpeciesToggleList({ d, patch }: {
  d: DetectorConfigOption
  patch: (p: DetectorConfigPatch) => void
}) {
  const { t } = useTranslation('cameras')
  const disabled = new Set(d.disabled_classes ?? [])
  const species = d.available_classes.filter((c) => !STRUCTURAL.has(c.category))
  if (species.length === 0) return null

  const setEnabled = (classId: number, enabled: boolean) => {
    const next = new Set(disabled)
    if (enabled) next.delete(classId)
    else next.add(classId)
    patch({ ai_model_id: d.ai_model_id, disabled_classes: Array.from(next).sort((a, b) => a - b) })
  }

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{t('detection.speciesHint')}</p>
      <div className="rounded-md border border-border/60 px-3">
        {species.map((c) => (
          <SwitchRow
            key={c.class_id}
            id={`sp-${d.ai_model_id}-${c.class_id}`}
            label={c.class_name}
            checked={!disabled.has(c.class_id)}
            onCheckedChange={(on) => setEnabled(c.class_id, on)}
          />
        ))}
      </div>
    </div>
  )
}
